<template>
  <v-container>
    <h1>Анализ загрузки кортов/столов</h1>

    <div class="mb-5">
      <router-link to="/reports">
        Назад к списку отчётов
      </router-link>
    </div>

    <my-loader :loader="loading"></my-loader>

    <!-- Блок ранее сформированных отчетов -->
    <v-card class="mb-4">
      <v-card-title>Ранее сформированные отчёты</v-card-title>

      <v-card-text>
        <v-progress-circular v-if="loading" indeterminate color="primary" size="40" class="my-4" />

        <!-- Таблица списка отчетов -->
        <v-data-table v-if="!loading" :headers="reportsHeaders" :items="reportsList" hide-default-footer
          :items-per-page="-1">
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:item.period="{ item }">
            {{ formatDate(item.date_from) }} — {{ formatDate(item.date_to) }}
          </template>

          <template v-slot:item.sport="{ item }">
            {{ item.sport }}
          </template>

          <template v-slot:item.report_status="{ item }">
            <span class="d-flex align-center">
              {{ getStatusText(item.report_status) }}
              <v-progress-circular v-if="item.report_status === 'PROCESSING'" indeterminate color="primary" size="16"
                width="2" class="ml-2" />
            </span>
          </template>

          <template v-slot:item.action="{ item }">
            <a v-if="item.report_status != 'PROCESSING'" :href="'https://lk.xbadm.ru' + item.url">
              Скачать
            </a>
          </template>
        </v-data-table>

      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>Запросить новый отчет</v-card-title>
      <v-card-text>
        <!-- Выбор вида спорта -->
        <v-row>
          <v-col cols="12">
            <v-select v-model="selectedSport" :items="sport_list" label="Вид спорта" outlined dense required></v-select>
          </v-col>
        </v-row>

        <!-- Выбор дат -->
        <v-row>
          <v-col cols="12" sm="6">
            <v-menu v-model="menuFrom" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="dateFrom" label="Неделя с" readonly v-bind="attrs" v-on="on"></v-text-field>
              </template>
              <v-date-picker v-model="calendarSelectedDate" @input="setWeekRange" :first-day-of-week="1"
                :events="weekRangeDates" event-color="primary" />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field :value="dateTo" label="по" readonly></v-text-field>
          </v-col>
        </v-row>

        <v-btn color="primary" @click="createNewReport" :disabled="!selectedSport || !dateFrom || !dateTo"
          :loading="generatingReport">
          Сформировать отчет
        </v-btn>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import Api from '@/api/api'
import MyLoader from '@/components/MyLoader.vue'

export default {
  name: 'CourtLoadingAnalytic',
  components: {
    MyLoader,
  },

  data() {
    return {
      branchId: Number(this.$route.params.branch_id),
      loading: false,
      generatingReport: false,
      menuFrom: false,
      menuTo: false,
      dateFrom: null,
      dateTo: null,
      reportsList: [],
      currentReport: null,
      activeReportId: null,
      selectedTrainer: null,
      detailsDialog: false,
      pollingInterval: null,
      selectedSport: null,
      sport_list: [
        'badminton',
        'table_tennis',
        'squash',
      ],
      reportsHeaders: [
        { text: 'Создан', value: 'created_at' },
        { text: 'Период', value: 'period' },
        { text: 'Вид спорта', value: 'sport' },
        { text: 'Статус', value: 'report_status' },
        { text: 'Действие', value: 'action', sortable: false }
      ],
      calendarSelectedDate: null,
    }
  },

  async created() {
    await this.loadReports()
  },

  beforeDestroy() {
    this.stopPolling()
  },
  computed: {
    weekRangeDates() {
      if (!this.dateFrom || !this.dateTo) return []

      const dates = []
      const current = new Date(this.dateFrom)

      while (current <= new Date(this.dateTo)) {
        dates.push(current.toISOString().slice(0, 10))
        current.setDate(current.getDate() + 1)
      }

      return dates
    }
  },

  methods: {
    setWeekRange(selectedDate) {
      this.calendarSelectedDate = selectedDate

      const selected = new Date(selectedDate)
      const jsDay = selected.getDay()

      // Расчёт смещения до понедельника
      const offset = jsDay === 0 ? -6 : 1 - jsDay
      const monday = new Date(selected)
      monday.setDate(selected.getDate() + offset + 1)
      monday.setHours(0, 0, 0, 0)

      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 5)
      sunday.setHours(23, 59, 59, 999)

      const mondayStr = monday.toISOString().slice(0, 10)
      const sundayStr = sunday.toISOString().slice(0, 10)

      this.dateFrom = mondayStr
      this.dateTo = sundayStr
      this.menuFrom = false
    },

    // Загрузка списка отчетов
    async loadReports() {
      this.loading = true
      const params = new URLSearchParams([['branch_id', this.$route.params.branch_id]])
      try {
        const response = await Api.getCourtsOccupancyReports({ params })
        this.reportsList = response || []

        // Если есть отчеты в обработке, запускаем опрос
        this.checkAndStartPolling()
      } catch (error) {
        console.error('Ошибка загрузки отчетов:', error)
        this.$toast.error(error.response?.data?.message || 'Не удалось загрузить список отчетов')
      } finally {
        this.loading = false
      }
    },

    // Проверка и запуск опроса для отчетов в обработке
    checkAndStartPolling() {
      const hasProcessing = this.reportsList.some(r => r.report_status === 'PROCESSING')

      if (hasProcessing && !this.pollingInterval) {
        this.pollingInterval = setInterval(() => {
          this.loadReports()
        }, 10000)
      } else if (!hasProcessing && this.pollingInterval) {
        this.stopPolling()
      }
    },

    // Остановка опроса
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    // Создание нового отчета
    async createNewReport() {
      try {
        await Api.createCourtsOccupancyReport({
          branch_id: this.branchId,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          sport: this.selectedSport,
        })
        await this.loadReports()
      } catch (error) {
        console.error('Ошибка создания отчета:', error)
        const errorMessage = error.response?.data?.message ||
          error.message ||
          'Не удалось создать отчет'
        this.$toast.error(errorMessage)
      } finally {
        this.generatingReport = false
      }
    },

    formatDateOnly(dateString) {
      if (!dateString) return ''

      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day)

      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    // Форматирование даты
    formatDate(dateString) {
      if (!dateString) return ''

      // Разбиваем YYYY-MM-DD вручную
      const [year, month, day] = dateString.split('-').map(Number)
      // Создаем дату в локальном времени
      const date = new Date(year, month - 1, day)

      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    },

    // Форматирование даты и времени
    formatDateTime(dateString) {
      if (!dateString) return ''

      const [datePart, timePart] = dateString.split('T')
      const [year, month, day] = datePart.split('-').map(Number)

      let hours = 0
      let minutes = 0

      if (timePart) {
        [hours, minutes] = timePart.split(':').map(Number)
      }

      // Создаем дату в локальном времени
      const date = new Date(year, month - 1, day, hours, minutes)

      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    // Текст статуса отчета
    getStatusText(status) {
      switch (status) {
        case 'PROCESSING': return 'Формируется'
        case 'READY': return 'Готов'
        case 'ERROR': return 'Ошибка'
        default: return status
      }
    },
  },
}
</script>

<style scoped>
.margin-btn {
  margin-left: 20px;
  margin-bottom: 10px;
}
</style>
